import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useCart } from "context/CartContext";
import { useCurrency } from "context/CurrencyContext";
import { useAuth } from "context/AuthContext";
import { useResolvedStorefrontLayout } from "storefronts/StorefrontLayoutBridge";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { storefrontThemeForTenant } from "lib/storefrontTheme";

const BRAND = {
  ink: "var(--storefront-ink, #0b1118)",
  muted: "var(--storefront-muted, #667085)",
  line: "var(--storefront-line, rgba(11,17,24,0.1))",
  soft: "var(--storefront-canvas, #f6f7f9)",
  red: "var(--storefront-accent, #cf241f)",
};

function currencyText(value, currency = "USD") {
  const n = Number(value || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(n);
  } catch {
    return `$${n.toFixed(2)}`;
  }
}

export default function Cart() {
  const StorefrontLayout = useResolvedStorefrontLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, locale } = useStorefrontCopy();
  const { tenant } = useStorefrontTenant();
  const storefrontTheme = storefrontThemeForTenant(tenant, locale);
  const {
    lines,
    removeItem,
    setQty,
    clear,
    itemCount,
    subtotal,
    itemSubtotal,
    coreSubtotal,
  } = useCart();
  const { formatUSDForDisplay } = useCurrency();
  const { activeCompany, storefrontTenant, token } = useAuth();
  const isMobileRoute =
    location.pathname === "/m/cart" || location.pathname.startsWith("/m/cart/");
  const toPath = (path) =>
    isMobileRoute ? `/m${path === "/" ? "" : path}` : path;
  const currency = currencyForCompany(storefrontTenant || activeCompany);

  const hasItems = (lines || []).length > 0;

  const totals = useMemo(() => {
    return {
      itemCount,
      subtotal,
      itemSubtotal,
      coreSubtotal,
    };
  }, [itemCount, subtotal, itemSubtotal, coreSubtotal]);

  if (!token) {
    return (
      <StorefrontLayout>
        <Box
          sx={{
            minHeight: "70vh",
            bgcolor: isMobileRoute ? "#fbfbfa" : "transparent",
          }}
        >
          <Container
            maxWidth={isMobileRoute ? "sm" : "md"}
            sx={{ px: 2, py: isMobileRoute ? 3 : 6 }}
          >
            <MDBox
              sx={{
                borderTop: `1px solid ${BRAND.line}`,
                borderBottom: `1px solid ${BRAND.line}`,
                py: 3,
              }}
            >
              <MDTypography
                variant="h4"
                sx={{ color: BRAND.ink, fontWeight: 900, mb: 1 }}
              >
                Sign in to use cart
              </MDTypography>
              <MDTypography
                variant="body2"
                sx={{ color: BRAND.muted, lineHeight: 1.6, mb: 2 }}
              >
                Retail pricing is visible in the catalog, but ordering and
                checkout require a customer account.
              </MDTypography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <MDButton
                  variant="contained"
                  sx={{
                    bgcolor: BRAND.ink,
                    color: "#fff",
                    borderRadius: 1,
                    fontWeight: 900,
                    boxShadow: "none",
                  }}
                  onClick={() =>
                    navigate(toPath("/authentication/sign-in?next=/m/cart"))
                  }
                >
                  Sign in
                </MDButton>
                <MDButton
                  variant="outlined"
                  sx={{
                    color: BRAND.ink,
                    borderColor: BRAND.line,
                    borderRadius: 1,
                    fontWeight: 900,
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  Back to catalog
                </MDButton>
              </Stack>
            </MDBox>
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (isMobileRoute) {
    return (
      <StorefrontLayout>
        <Box
          sx={{ minHeight: "100vh", bgcolor: "#fbfbfa", pb: hasItems ? 11 : 3 }}
        >
          <Container maxWidth="sm" sx={{ px: 2, pt: 2 }}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.25}
              mb={2}
            >
              <MDBox>
                <MDTypography
                  variant="caption"
                  sx={{ color: BRAND.red, fontWeight: 900 }}
                >
                  CART
                </MDTypography>
                <MDTypography
                  variant="h4"
                  sx={{ color: BRAND.ink, fontWeight: 900, lineHeight: 1.05 }}
                >
                  Review order
                </MDTypography>
              </MDBox>
              <Stack direction="row" spacing={0.75}>
                <MDButton
                  variant="outlined"
                  sx={{
                    minWidth: 44,
                    width: 44,
                    height: 44,
                    p: 0,
                    borderRadius: 2,
                    color: BRAND.ink,
                    borderColor: BRAND.line,
                    bgcolor: "#fff",
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                  aria-label="Continue shopping"
                >
                  <Icon>storefront</Icon>
                </MDButton>
                {hasItems ? (
                  <MDButton
                    variant="outlined"
                    sx={{
                      minWidth: 44,
                      width: 44,
                      height: 44,
                      p: 0,
                      borderRadius: 2,
                      color: BRAND.ink,
                      borderColor: BRAND.line,
                      bgcolor: "#fff",
                    }}
                    onClick={clear}
                    aria-label="Clear cart"
                  >
                    <Icon>delete_sweep</Icon>
                  </MDButton>
                ) : null}
              </Stack>
            </MDBox>

            {!hasItems ? (
              <Card
                variant="outlined"
                sx={{
                  p: 2.25,
                  borderRadius: 3,
                  borderColor: BRAND.line,
                  boxShadow: "0 12px 30px rgba(11,17,24,0.06)",
                }}
              >
                <MDBox
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2.5,
                    bgcolor: "rgba(207,36,31,0.08)",
                    display: "grid",
                    placeItems: "center",
                    mb: 1.5,
                  }}
                >
                  <Icon sx={{ color: BRAND.red, fontSize: 30 }}>
                    shopping_cart
                  </Icon>
                </MDBox>
                <MDTypography
                  variant="h6"
                  sx={{ color: BRAND.ink, fontWeight: 900, mb: 0.5 }}
                >
                  Your cart is empty
                </MDTypography>
                <MDTypography
                  variant="body2"
                  sx={{ color: BRAND.muted, lineHeight: 1.55, mb: 2 }}
                >
                  Add catalog items and come back here to check out.
                </MDTypography>
                <MDButton
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor: BRAND.red,
                    color: "#fff",
                    borderRadius: 2,
                    py: 1.25,
                    fontWeight: 900,
                    boxShadow: "none",
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  Browse catalog
                </MDButton>
              </Card>
            ) : (
              <>
                <Stack spacing={1.25}>
                  {(lines || []).map((l) => {
                    const qty = Number(l.qty || 0) || 0;
                    const unit = Number(l.unitPrice || 0) || 0;
                    const core = Number(l.coreCharge || 0) || 0;
                    const lineTotal = qty * (unit + core);
                    return (
                      <Card
                        key={l.documentId}
                        variant="outlined"
                        sx={{
                          borderRadius: 3,
                          borderColor: BRAND.line,
                          boxShadow: "0 12px 30px rgba(11,17,24,0.06)",
                          overflow: "hidden",
                        }}
                      >
                        <MDBox sx={{ p: 1.25 }}>
                          <MDBox display="flex" gap={1.25}>
                            {l.imageUrl ? (
                              <Box
                                component="img"
                                src={l.imageUrl}
                                alt={l.name || l.sku || "Item"}
                                sx={{
                                  width: 82,
                                  height: 82,
                                  objectFit: "cover",
                                  borderRadius: 2,
                                  bgcolor: BRAND.soft,
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <MDBox
                                sx={{
                                  width: 82,
                                  height: 82,
                                  borderRadius: 2,
                                  bgcolor: "rgba(207,36,31,0.08)",
                                  display: "grid",
                                  placeItems: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <Icon sx={{ color: BRAND.red, fontSize: 30 }}>
                                  inventory_2
                                </Icon>
                              </MDBox>
                            )}
                            <MDBox sx={{ minWidth: 0, flex: 1 }}>
                              <MDTypography
                                variant="button"
                                sx={{
                                  display: "block",
                                  color: BRAND.ink,
                                  fontWeight: 900,
                                  lineHeight: 1.25,
                                }}
                              >
                                {l.name || l.sku || "Item"}
                              </MDTypography>
                              {l.sku ? (
                                <MDTypography
                                  variant="caption"
                                  sx={{
                                    color: BRAND.muted,
                                    display: "block",
                                    mt: 0.25,
                                  }}
                                >
                                  SKU {l.sku}
                                </MDTypography>
                              ) : null}
                              <MDTypography
                                variant="caption"
                                sx={{
                                  color: BRAND.muted,
                                  display: "block",
                                  mt: 0.5,
                                }}
                              >
                                {currencyText(unit, currency)}
                                {core
                                  ? ` + core ${currencyText(core, currency)}`
                                  : ""}
                              </MDTypography>
                            </MDBox>
                            <IconButton
                              onClick={() => removeItem(l.documentId)}
                              size="small"
                              aria-label="Remove"
                              sx={{
                                alignSelf: "flex-start",
                                color: BRAND.muted,
                              }}
                            >
                              <Icon>close</Icon>
                            </IconButton>
                          </MDBox>
                          <Divider sx={{ my: 1.25 }} />
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1.25}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.75}
                            >
                              <MDButton
                                variant="outlined"
                                sx={{
                                  minWidth: 38,
                                  width: 38,
                                  height: 38,
                                  p: 0,
                                  borderRadius: 2,
                                  color: BRAND.ink,
                                  borderColor: BRAND.line,
                                }}
                                onClick={() =>
                                  setQty(l.documentId, Math.max(1, qty - 1))
                                }
                              >
                                <Icon>remove</Icon>
                              </MDButton>
                              <TextField
                                type="number"
                                value={l.qty}
                                onChange={(e) =>
                                  setQty(l.documentId, e.target.value)
                                }
                                inputProps={{
                                  min: 1,
                                  step: 1,
                                  inputMode: "numeric",
                                  style: { textAlign: "center" },
                                }}
                                sx={{
                                  width: 68,
                                  "& .MuiOutlinedInput-root": {
                                    height: 38,
                                    borderRadius: 2,
                                  },
                                  "& input": { py: 0, fontWeight: 900 },
                                }}
                              />
                              <MDButton
                                variant="outlined"
                                sx={{
                                  minWidth: 38,
                                  width: 38,
                                  height: 38,
                                  p: 0,
                                  borderRadius: 2,
                                  color: BRAND.ink,
                                  borderColor: BRAND.line,
                                }}
                                onClick={() => setQty(l.documentId, qty + 1)}
                              >
                                <Icon>add</Icon>
                              </MDButton>
                            </Stack>
                            <MDTypography
                              variant="h6"
                              sx={{ color: BRAND.ink, fontWeight: 900 }}
                            >
                              {currencyText(lineTotal, currency)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                      </Card>
                    );
                  })}
                </Stack>

                <Card
                  variant="outlined"
                  sx={{
                    mt: 1.5,
                    p: 2,
                    borderRadius: 3,
                    borderColor: BRAND.line,
                    boxShadow: "none",
                  }}
                >
                  <MDTypography
                    variant="h6"
                    sx={{ color: BRAND.ink, fontWeight: 900, mb: 1 }}
                  >
                    Summary
                  </MDTypography>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    py={0.65}
                  >
                    <MDTypography
                      variant="body2"
                      sx={{ color: BRAND.muted, fontWeight: 700 }}
                    >
                      Items
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      sx={{ color: BRAND.ink, fontWeight: 900 }}
                    >
                      {totals.itemCount}
                    </MDTypography>
                  </MDBox>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    py={0.65}
                  >
                    <MDTypography
                      variant="body2"
                      sx={{ color: BRAND.muted, fontWeight: 700 }}
                    >
                      Subtotal
                    </MDTypography>
                    <MDTypography
                      variant="body2"
                      sx={{ color: BRAND.ink, fontWeight: 900 }}
                    >
                      {currencyText(totals.itemSubtotal, currency)}
                    </MDTypography>
                  </MDBox>
                  {totals.coreSubtotal > 0 ? (
                    <MDBox
                      display="flex"
                      justifyContent="space-between"
                      py={0.65}
                      gap={2}
                    >
                      <MDTypography
                        variant="body2"
                        sx={{ color: BRAND.muted, fontWeight: 700 }}
                      >
                        Refundable core deposits
                      </MDTypography>
                      <MDTypography
                        variant="body2"
                        sx={{ color: BRAND.ink, fontWeight: 900 }}
                      >
                        {currencyText(totals.coreSubtotal, currency)}
                      </MDTypography>
                    </MDBox>
                  ) : null}
                </Card>
              </>
            )}
          </Container>

          {hasItems ? (
            <Box
              sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1300,
                bgcolor: "#fff",
                borderTop: `1px solid ${BRAND.line}`,
                boxShadow: "0 -12px 34px rgba(11,17,24,0.12)",
                px: 2,
                pt: 1.25,
                pb: "calc(10px + env(safe-area-inset-bottom))",
              }}
            >
              <Container maxWidth="sm" sx={{ px: 0 }}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <MDBox sx={{ minWidth: 0, flex: 1 }}>
                    <MDTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: BRAND.muted,
                        fontWeight: 800,
                        lineHeight: 1.1,
                      }}
                    >
                      {t("cart.total", "Cart total")}
                    </MDTypography>
                    <MDTypography
                      variant="h6"
                      sx={{
                        color: BRAND.ink,
                        fontWeight: 900,
                        lineHeight: 1.2,
                      }}
                    >
                      {currencyText(totals.subtotal, currency)}
                    </MDTypography>
                  </MDBox>
                  <MDButton
                    variant="contained"
                    sx={{
                      bgcolor: BRAND.red,
                      color: "#fff",
                      minWidth: 154,
                      height: 46,
                      borderRadius: 2,
                      fontWeight: 900,
                      boxShadow: "none",
                    }}
                    onClick={() =>
                      navigate(
                        toPath(
                          token
                            ? "/checkout"
                            : `/authentication/sign-in?next=${encodeURIComponent(
                                toPath("/checkout"),
                              )}`,
                        ),
                      )
                    }
                  >
                    {t("cart.checkout", "Checkout")}
                  </MDButton>
                </Stack>
              </Container>
            </Box>
          ) : null}
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "industrial") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box
          sx={{
            bgcolor:
              "radial-gradient(circle at 90% 4%, rgba(181,71,8,0.14), transparent 34%), #080B0E",
            minHeight: "100vh",
            py: 4,
          }}
        >
          <Container maxWidth="xl">
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              gap={2}
              sx={{
                bgcolor: "transparent",
                color: "#fff",
                p: { xs: 2, md: 3 },
                border: 0,
                borderBottom: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 0,
                mb: 2.5,
                boxShadow: "none",
              }}
            >
              <Box>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.62)",
                    fontWeight: 900,
                    fontFamily: "monospace",
                  }}
                >
                  ORDER WORKSHEET / {totals.itemCount} UNITS
                </MDTypography>
                <MDTypography
                  variant="h2"
                  sx={{ color: "#fff", fontWeight: 950, lineHeight: 1 }}
                >
                  Cart review
                </MDTypography>
              </Box>
              <Stack direction="row" spacing={1}>
                <MDButton
                  variant="outlined"
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.28)",
                    borderRadius: 0,
                    fontWeight: 900,
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  Continue sourcing
                </MDButton>
                {hasItems ? (
                  <MDButton
                    variant="outlined"
                    sx={{
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.28)",
                      borderRadius: 0,
                      fontWeight: 900,
                    }}
                    onClick={clear}
                  >
                    Clear worksheet
                  </MDButton>
                ) : null}
              </Stack>
            </Stack>

            {!hasItems ? (
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.94)",
                  border: 0,
                  borderRadius: 0,
                  p: 3,
                }}
              >
                <MDTypography
                  variant="h5"
                  sx={{ color: BRAND.ink, fontWeight: 950, mb: 1 }}
                >
                  {t("cart.emptyTitle", "Your cart is empty.")}
                </MDTypography>
                <MDButton
                  variant="contained"
                  sx={{
                    bgcolor: BRAND.red,
                    color: "#fff",
                    borderRadius: 0.75,
                    fontWeight: 900,
                    boxShadow: "none",
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  {t("common.browseCatalog", "Browse catalog")}
                </MDButton>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} lg={8}>
                  <Box
                    sx={{
                      bgcolor: "rgba(255,255,255,0.94)",
                      border: 0,
                      borderRadius: 0,
                      overflow: "hidden",
                      boxShadow: "0 24px 70px rgba(0,0,0,0.18)",
                    }}
                  >
                    <Box
                      sx={{
                        display: { xs: "none", md: "grid" },
                        gridTemplateColumns: "1.5fr 0.7fr 0.7fr 0.7fr auto",
                        gap: 1,
                        px: 2,
                        py: 1,
                        bgcolor: "rgba(8,11,14,0.055)",
                        borderBottom: "1px solid rgba(23,26,24,0.16)",
                      }}
                    >
                      {["Product", "SKU", "Qty", "Line total", ""].map(
                        (label) => (
                          <MDTypography
                            key={label || "actions"}
                            variant="caption"
                            sx={{
                              color: BRAND.muted,
                              fontWeight: 900,
                              fontFamily: "monospace",
                            }}
                          >
                            {label}
                          </MDTypography>
                        ),
                      )}
                    </Box>
                    {(lines || []).map((line) => {
                      const lineTotal =
                        (Number(line.qty || 0) || 0) *
                        ((Number(line.unitPrice || 0) || 0) +
                          (Number(line.coreCharge || 0) || 0));
                      return (
                        <Box
                          key={line.documentId}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "72px 1fr",
                              md: "1.5fr 0.7fr 0.7fr 0.7fr auto",
                            },
                            gap: 1.5,
                            alignItems: "center",
                            px: 2,
                            py: 1.5,
                            borderBottom: "1px solid rgba(23,26,24,0.12)",
                          }}
                        >
                          <Stack direction="row" spacing={1.25} minWidth={0}>
                            {line.imageUrl ? (
                              <Box
                                component="img"
                                src={line.imageUrl}
                                alt={line.name || line.sku || "Item"}
                                sx={{
                                  width: 64,
                                  height: 64,
                                  objectFit: "contain",
                                  bgcolor: "#f4f5f2",
                                  border: 0,
                                  flexShrink: 0,
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  display: "grid",
                                  placeItems: "center",
                                  bgcolor: "#f4f5f2",
                                  border: 0,
                                  flexShrink: 0,
                                }}
                              >
                                <Icon sx={{ color: BRAND.muted }}>
                                  inventory_2
                                </Icon>
                              </Box>
                            )}
                            <Box minWidth={0}>
                              <MDTypography
                                variant="body2"
                                sx={{
                                  color: BRAND.ink,
                                  fontWeight: 950,
                                  lineHeight: 1.25,
                                }}
                              >
                                {line.name || line.sku || "Item"}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                sx={{ color: BRAND.muted, fontWeight: 800 }}
                              >
                                Unit {currencyText(line.unitPrice, currency)}
                                {line.coreCharge
                                  ? ` + core ${currencyText(
                                      line.coreCharge,
                                      currency,
                                    )}`
                                  : ""}
                              </MDTypography>
                            </Box>
                          </Stack>
                          <MDTypography
                            variant="caption"
                            sx={{
                              color: BRAND.ink,
                              fontWeight: 900,
                              fontFamily: "monospace",
                            }}
                          >
                            {line.sku || "PENDING"}
                          </MDTypography>
                          <TextField
                            size="small"
                            type="number"
                            value={line.qty}
                            onChange={(event) =>
                              setQty(line.documentId, event.target.value)
                            }
                            inputProps={{ min: 1, step: 1 }}
                            sx={{ width: 88 }}
                          />
                          <MDTypography
                            variant="body2"
                            sx={{ color: BRAND.ink, fontWeight: 950 }}
                          >
                            {currencyText(lineTotal, currency)}
                          </MDTypography>
                          <IconButton
                            onClick={() => removeItem(line.documentId)}
                            size="small"
                            aria-label="Remove"
                          >
                            <Icon fontSize="small">delete</Icon>
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>
                <Grid item xs={12} lg={4}>
                  <Box
                    sx={{
                      bgcolor: "rgba(255,255,255,0.08)",
                      color: "#fff",
                      p: 2.5,
                      border: 0,
                      borderRadius: 0,
                      boxShadow: "0 24px 70px rgba(0,0,0,0.20)",
                      position: { lg: "sticky" },
                      top: 104,
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.62)",
                        fontWeight: 900,
                        fontFamily: "monospace",
                      }}
                    >
                      ORDER TOTAL
                    </MDTypography>
                    <MDTypography
                      variant="h2"
                      sx={{ color: "#fff", fontWeight: 950, mb: 2 }}
                    >
                      {currencyText(totals.subtotal, currency)}
                    </MDTypography>
                    {[
                      ["Items", totals.itemCount],
                      [
                        "Parts subtotal",
                        currencyText(totals.itemSubtotal, currency),
                      ],
                      [
                        "Refundable core",
                        currencyText(totals.coreSubtotal, currency),
                      ],
                    ].map(([label, value]) => (
                      <Stack
                        key={label}
                        direction="row"
                        justifyContent="space-between"
                        sx={{
                          py: 1,
                          borderTop: "1px solid rgba(255,255,255,0.12)",
                        }}
                      >
                        <MDTypography
                          variant="body2"
                          sx={{ color: "rgba(255,255,255,0.62)" }}
                        >
                          {label}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          sx={{ color: "#fff", fontWeight: 900 }}
                        >
                          {value}
                        </MDTypography>
                      </Stack>
                    ))}
                    <MDButton
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: 2,
                        bgcolor: BRAND.red,
                        color: "#fff",
                        borderRadius: 0,
                        minHeight: 48,
                        fontWeight: 900,
                        boxShadow: "none",
                      }}
                      onClick={() => navigate(toPath("/checkout"))}
                    >
                      {t("cart.checkout", "Checkout")}
                    </MDButton>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  if (storefrontTheme.preset === "commerce") {
    return (
      <StorefrontLayout maxWidth={null}>
        <Box sx={{ bgcolor: "#F7F9FC", minHeight: "100vh", py: 4 }}>
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "flex-end" }}
              gap={2}
              mb={3}
            >
              <Box>
                <MDTypography
                  variant="caption"
                  sx={{ color: BRAND.red, fontWeight: 900 }}
                >
                  {totals.itemCount} items ready
                </MDTypography>
                <MDTypography
                  variant="h2"
                  sx={{ color: BRAND.ink, fontWeight: 950, lineHeight: 0.98 }}
                >
                  {t("cart.title", "Cart")}
                </MDTypography>
              </Box>
              <Stack direction="row" spacing={1}>
                <MDButton
                  variant="outlined"
                  sx={{
                    color: BRAND.ink,
                    borderColor: BRAND.line,
                    borderRadius: 2,
                    fontWeight: 900,
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  {t("common.browseCatalog", "Continue shopping")}
                </MDButton>
                {hasItems ? (
                  <MDButton
                    variant="outlined"
                    sx={{
                      color: BRAND.ink,
                      borderColor: BRAND.line,
                      borderRadius: 2,
                      fontWeight: 900,
                    }}
                    onClick={clear}
                  >
                    Clear
                  </MDButton>
                ) : null}
              </Stack>
            </Stack>

            {!hasItems ? (
              <Box
                sx={{
                  bgcolor: "#fff",
                  border: 0,
                  borderRadius: 0,
                  p: 4,
                  textAlign: "center",
                  boxShadow: "0 18px 50px rgba(15,23,42,0.06)",
                }}
              >
                <Icon sx={{ fontSize: 54, color: BRAND.red, mb: 1 }}>
                  shopping_cart
                </Icon>
                <MDTypography
                  variant="h4"
                  sx={{ color: BRAND.ink, fontWeight: 950, mb: 1 }}
                >
                  {t("cart.emptyTitle", "Your cart is empty.")}
                </MDTypography>
                <MDButton
                  variant="contained"
                  sx={{
                    bgcolor: BRAND.red,
                    color: "#fff",
                    borderRadius: 2,
                    fontWeight: 900,
                    boxShadow: "none",
                  }}
                  onClick={() => navigate(toPath("/catalog"))}
                >
                  {t("common.browseCatalog", "Browse catalog")}
                </MDButton>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={7}>
                  <Stack spacing={1.5}>
                    {(lines || []).map((line) => {
                      const lineTotal =
                        (Number(line.qty || 0) || 0) *
                        ((Number(line.unitPrice || 0) || 0) +
                          (Number(line.coreCharge || 0) || 0));
                      return (
                        <Box
                          key={line.documentId}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "92px 1fr",
                              sm: "120px 1fr auto",
                            },
                            gap: 1.5,
                            bgcolor: "#fff",
                            border: 0,
                            borderRadius: 0,
                            p: 1.5,
                            boxShadow: "0 18px 50px rgba(16,24,40,0.08)",
                          }}
                        >
                          {line.imageUrl ? (
                            <Box
                              component="img"
                              src={line.imageUrl}
                              alt={line.name || line.sku || "Item"}
                              sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                objectFit: "contain",
                                bgcolor: "#f8fafc",
                                borderRadius: 2,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                aspectRatio: "1 / 1",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: "#f8fafc",
                                borderRadius: 2,
                              }}
                            >
                              <Icon sx={{ color: BRAND.muted }}>
                                inventory_2
                              </Icon>
                            </Box>
                          )}
                          <Box minWidth={0}>
                            <MDTypography
                              variant="h6"
                              sx={{ color: BRAND.ink, fontWeight: 950 }}
                            >
                              {line.name || line.sku || "Item"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              sx={{
                                color: BRAND.muted,
                                fontWeight: 800,
                                display: "block",
                              }}
                            >
                              SKU {line.sku || "Pending"}
                            </MDTypography>
                            <MDTypography
                              variant="body2"
                              sx={{ color: BRAND.muted, mt: 0.5 }}
                            >
                              {currencyText(line.unitPrice, currency)}
                              {line.coreCharge
                                ? ` + core ${currencyText(
                                    line.coreCharge,
                                    currency,
                                  )}`
                                : ""}
                            </MDTypography>
                            <Stack direction="row" spacing={1} mt={1}>
                              <TextField
                                size="small"
                                type="number"
                                value={line.qty}
                                onChange={(event) =>
                                  setQty(line.documentId, event.target.value)
                                }
                                inputProps={{ min: 1, step: 1 }}
                                sx={{ width: 96 }}
                              />
                              <IconButton
                                onClick={() => removeItem(line.documentId)}
                                size="small"
                                aria-label="Remove"
                              >
                                <Icon fontSize="small">delete</Icon>
                              </IconButton>
                            </Stack>
                          </Box>
                          <MDTypography
                            variant="h6"
                            sx={{
                              color: BRAND.ink,
                              fontWeight: 950,
                              textAlign: { xs: "left", sm: "right" },
                            }}
                          >
                            {currencyText(lineTotal, currency)}
                          </MDTypography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      bgcolor: "#fff",
                      border: 0,
                      borderRadius: 0,
                      p: 2.5,
                      boxShadow: "0 28px 90px rgba(16,24,40,0.10)",
                      position: { md: "sticky" },
                      top: 104,
                    }}
                  >
                    <MDTypography
                      variant="h4"
                      sx={{ color: BRAND.ink, fontWeight: 950, mb: 2 }}
                    >
                      {t("cart.summary", "Summary")}
                    </MDTypography>
                    {[
                      ["Items", totals.itemCount],
                      ["Subtotal", currencyText(totals.itemSubtotal, currency)],
                      [
                        "Refundable deposits",
                        currencyText(totals.coreSubtotal, currency),
                      ],
                    ].map(([label, value]) => (
                      <Stack
                        key={label}
                        direction="row"
                        justifyContent="space-between"
                        sx={{ py: 1, borderTop: `1px solid ${BRAND.line}` }}
                      >
                        <MDTypography
                          variant="body2"
                          sx={{ color: BRAND.muted, fontWeight: 800 }}
                        >
                          {label}
                        </MDTypography>
                        <MDTypography
                          variant="body2"
                          sx={{ color: BRAND.ink, fontWeight: 950 }}
                        >
                          {value}
                        </MDTypography>
                      </Stack>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <Stack direction="row" justifyContent="space-between">
                      <MDTypography
                        variant="h6"
                        sx={{ color: BRAND.ink, fontWeight: 950 }}
                      >
                        {t("cart.total", "Cart total")}
                      </MDTypography>
                      <MDTypography
                        variant="h5"
                        sx={{ color: BRAND.red, fontWeight: 950 }}
                      >
                        {currencyText(totals.subtotal, currency)}
                      </MDTypography>
                    </Stack>
                    <MDButton
                      variant="contained"
                      fullWidth
                      sx={{
                        mt: 2,
                        bgcolor: BRAND.red,
                        color: "#fff",
                        borderRadius: 2,
                        minHeight: 50,
                        fontWeight: 900,
                        boxShadow: "none",
                      }}
                      onClick={() => navigate(toPath("/checkout"))}
                    >
                      {t("cart.checkout", "Checkout")}
                    </MDButton>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Container>
        </Box>
      </StorefrontLayout>
    );
  }

  return (
    <StorefrontLayout>
      <MDBox
        mb={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1.5}
      >
        <MDTypography variant="h4" fontWeight={900}>
          {t("cart.title", "Cart")}
        </MDTypography>
        <MDBox display="flex" gap={1}>
          <MDButton
            variant="text"
            color="info"
            onClick={() => navigate(toPath("/catalog"))}
          >
            {t("common.browseCatalog", "Continue shopping")}
          </MDButton>
          {hasItems ? (
            <MDButton variant="outlined" color="secondary" onClick={clear}>
              Clear
            </MDButton>
          ) : null}
        </MDBox>
      </MDBox>

      {!hasItems ? (
        <Card variant="outlined" sx={{ p: 3 }}>
          <MDTypography variant="body1" color="text">
            {t("cart.emptyTitle", "Your cart is empty.")}
          </MDTypography>
          <MDBox mt={2}>
            <MDButton
              variant="contained"
              color="info"
              onClick={() => navigate(toPath("/catalog"))}
            >
              {t("common.browseCatalog", "Browse catalog")}
            </MDButton>
          </MDBox>
        </Card>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ p: 2 }}>
              {(lines || []).map((l) => {
                const lineTotal =
                  (Number(l.qty || 0) || 0) *
                  ((Number(l.unitPrice || 0) || 0) +
                    (Number(l.coreCharge || 0) || 0));
                return (
                  <Box key={l.documentId}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                      py={1.5}
                    >
                      {l.imageUrl ? (
                        <Box
                          component="img"
                          src={l.imageUrl}
                          alt={l.name || l.sku || "Item"}
                          sx={{
                            width: 64,
                            height: 64,
                            objectFit: "cover",
                            borderRadius: 2,
                            bgcolor: "#f3f4f6",
                            flexShrink: 0,
                          }}
                        />
                      ) : (
                        <MDBox
                          sx={{
                            width: 64,
                            height: 64,
                            borderRadius: 2,
                            bgcolor: "rgba(215,38,30,0.08)",
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ color: "#d7261e" }}>inventory_2</Icon>
                        </MDBox>
                      )}
                      <MDBox sx={{ minWidth: 0 }}>
                        <MDTypography variant="h6" fontWeight={800} noWrap>
                          {l.name || l.sku || "Item"}
                        </MDTypography>
                        {l.sku ? (
                          <MDTypography
                            variant="caption"
                            color="text"
                            sx={{ opacity: 0.8, display: "block" }}
                          >
                            SKU: {l.sku}
                          </MDTypography>
                        ) : null}
                        <MDTypography
                          variant="body2"
                          color="text"
                          sx={{ opacity: 0.9, mt: 0.5 }}
                        >
                          Unit: {formatUSDForDisplay(Number(l.unitPrice || 0))}{" "}
                          {l.coreCharge
                            ? ` + Core ${formatUSDForDisplay(
                                Number(l.coreCharge || 0),
                              )}`
                            : ""}
                        </MDTypography>
                        {l.coreCharge ? (
                          <MDTypography
                            variant="caption"
                            color="text"
                            sx={{ opacity: 0.72, display: "block", mt: 0.35 }}
                          >
                            {t(
                              "cart.refundableDeposit",
                              "Deposit is refundable after return and inspection.",
                            )}
                          </MDTypography>
                        ) : null}
                      </MDBox>

                      <MDBox display="flex" alignItems="center" gap={1}>
                        <TextField
                          size="small"
                          type="number"
                          value={l.qty}
                          onChange={(e) => setQty(l.documentId, e.target.value)}
                          inputProps={{ min: 1, step: 1 }}
                          sx={{ width: 90 }}
                        />
                        <MDTypography
                          variant="body2"
                          fontWeight={800}
                          sx={{ minWidth: 120, textAlign: "right" }}
                        >
                          {formatUSDForDisplay(lineTotal)}
                        </MDTypography>
                        <IconButton
                          onClick={() => removeItem(l.documentId)}
                          size="small"
                          aria-label="Remove"
                        >
                          <Icon fontSize="small">delete</Icon>
                        </IconButton>
                      </MDBox>
                    </MDBox>
                    <Divider />
                  </Box>
                );
              })}
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card variant="outlined" sx={{ p: 2 }}>
              <MDTypography variant="h6" fontWeight={900} gutterBottom>
                {t("cart.summary", "Summary")}
              </MDTypography>
              <MDBox display="flex" justifyContent="space-between" py={0.75}>
                <MDTypography
                  variant="body2"
                  color="text"
                  sx={{ opacity: 0.85 }}
                >
                  Items
                </MDTypography>
                <MDTypography variant="body2" fontWeight={800}>
                  {totals.itemCount}
                </MDTypography>
              </MDBox>
              <MDBox display="flex" justifyContent="space-between" py={0.75}>
                <MDTypography
                  variant="body2"
                  color="text"
                  sx={{ opacity: 0.85 }}
                >
                  Subtotal
                </MDTypography>
                <MDTypography variant="body2" fontWeight={800}>
                  {formatUSDForDisplay(totals.itemSubtotal)}
                </MDTypography>
              </MDBox>
              {totals.coreSubtotal > 0 ? (
                <MDBox display="flex" justifyContent="space-between" py={0.75}>
                  <MDTypography
                    variant="body2"
                    color="text"
                    sx={{ opacity: 0.85 }}
                  >
                    Refundable core deposits
                  </MDTypography>
                  <MDTypography variant="body2" fontWeight={800}>
                    {formatUSDForDisplay(totals.coreSubtotal)}
                  </MDTypography>
                </MDBox>
              ) : null}
              <Divider sx={{ my: 1 }} />
              <MDBox display="flex" justifyContent="space-between" py={0.75}>
                <MDTypography variant="body2" color="text" fontWeight={900}>
                  {t("cart.total", "Cart total")}
                </MDTypography>
                <MDTypography variant="body2" fontWeight={900}>
                  {formatUSDForDisplay(totals.subtotal)}
                </MDTypography>
              </MDBox>
              <MDBox mt={2}>
                <MDButton
                  variant="contained"
                  color="info"
                  fullWidth
                  onClick={() => navigate(toPath("/checkout"))}
                >
                  {t("cart.checkout", "Checkout")}
                </MDButton>
              </MDBox>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ opacity: 0.75, display: "block", mt: 1 }}
              >
                {t(
                  "cart.signInRequired",
                  "Checkout will require signing in to a customer account.",
                )}
              </MDTypography>
            </Card>
          </Grid>
        </Grid>
      )}
    </StorefrontLayout>
  );
}
